import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined"
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"

import CloseableSnackbar, {
  useCloseableSnackbarState,
} from "../../../../../common/components/CloseableSnackbar"
import Column from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import {
  CancelCalendarMeetingDialog,
  RescheduleCalendarMeetingDialog,
} from "../../../components/CalendarMeetingDialogs"
import ConfirmConnectEmailDialog, {
  useConnectEmailDialogRequirements,
} from "../../../components/ConfirmConnectEmailDialog"
import {
  GoogleAuthProvider,
  MicrosoftProvider,
} from "../../../components/ConfirmConnectEmailDialog"
import CreateEventDialog, {
  useCreateEventDialogState,
} from "../../../components/CreateMeetingScheduleDialog"
import MeetingAttendeesStack from "../../../components/MeetingAttendeesStack"
import RescheduleBookingDialog from "../../../components/RescheduleBookingDialog"
import {
  canChangeCalendarMeeting,
  isCancelledMeeting,
  splitMeetings,
} from "../../../components/calendarMeetingUtils"
import { useScrollToTargetElementRef } from "../../../utils"
import { GLOBAL_SEARCH_URL_PARAMS } from "../../data"
import {
  AccordionDetailsContent,
  AccordionDetailsTypography,
  AccordionSummaryContent,
  StyledAccordion,
} from "./Accordions"
import { performedBySubtitle } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"
import ActivitySkeleton from "./components/ActivitySkeleton"

export default function MeetingScheduleTab({
  candidate,
  meetings,
  setMeetings,
  isLoading,
  readOnly = false,
  onBookingRescheduled,
  onMeetingChanged,
}) {
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null)
  const [calendarRescheduleMeeting, setCalendarRescheduleMeeting] =
    useState(null)
  const [calendarCancelMeeting, setCalendarCancelMeeting] = useState(null)
  const {
    createEventDialogOpen,
    setCreateEventDialogOpen,
    openCreateEventDialog,
  } = useCreateEventDialogState()
  const {
    confirmConnectEmailDialogOpen,
    emailAuthorized,
    handleConfirmConnectGmailDialogClosed,
    openConfirmConnectEmailDialog,
  } = useConnectEmailDialogRequirements()
  const {
    isOpen: isSnackbarOpen,
    open: openSnackbar,
    message: snackbarMessage,
    close: closeSnackbar,
  } = useCloseableSnackbarState()
  const sortedMeetings = meetings.sort((m1, m2) => {
    const m1Date = new Date(m1.start_datetime)
    const m2Date = new Date(m2.start_datetime)
    return m1Date < m2Date ? 1 : m1Date === m2Date ? 0 : -1
  })
  const { upcoming: upcomingMeetings, history: oldMeetings } =
    splitMeetings(sortedMeetings)
  // The candidate-level reschedule only moves the appointment date. A meeting
  // on the user's calendar is moved with its own Reschedule button instead.
  const hasChangeableCalendarMeeting = upcomingMeetings.some((meeting) =>
    canChangeCalendarMeeting(meeting)
  )
  const canRescheduleBooking =
    !readOnly && Boolean(candidate?.stamp) && !hasChangeableCalendarMeeting

  function openRescheduleForCandidate() {
    setRescheduleMeeting({ start_datetime: candidate.stamp })
  }

  function handleScheduleMeetingButtonClicked() {
    if (emailAuthorized) {
      openCreateEventDialog()
    } else {
      openConfirmConnectEmailDialog()
    }
  }

  function handleCreateEventDialogClosed(createdEvent) {
    if (createdEvent) {
      setMeetings([...meetings, createdEvent])
      openSnackbar("Meeting created successfully")
    }
    setCreateEventDialogOpen(false)
  }

  function handleRescheduleCompleted(payload) {
    onBookingRescheduled?.(payload)
    if (payload.meeting_schedule) {
      setMeetings((current) =>
        current.map((meeting) =>
          meeting.id === payload.meeting_schedule.id
            ? { ...meeting, ...payload.meeting_schedule }
            : meeting
        )
      )
    }
    openSnackbar("Appointment rescheduled")
    setRescheduleMeeting(null)
  }

  function handleCalendarMeetingChanged(updatedMeeting, message) {
    setMeetings((current) =>
      current.map((meeting) =>
        meeting.id === updatedMeeting.id
          ? { ...meeting, ...updatedMeeting }
          : meeting
      )
    )
    openSnackbar(message)
    // The appointment date and the timeline may have changed too.
    onMeetingChanged?.()
  }

  return (
    <TabColumn>
      <TabHeaderRow title="Meetings">
        {readOnly ? null : (
          <Row sx={{ gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {canRescheduleBooking ? (
              <LoadingButton
                variant="outlined"
                color="inherit"
                loading={isLoading}
                startIcon={<EventRepeatOutlinedIcon />}
                onClick={openRescheduleForCandidate}
              >
                Reschedule
              </LoadingButton>
            ) : null}
            <LoadingButton
              variant="outlined"
              color="inherit"
              loading={isLoading}
              startIcon={<CalendarTodayOutlinedIcon />}
              onClick={handleScheduleMeetingButtonClicked}
            >
              Schedule Meeting
            </LoadingButton>
          </Row>
        )}
        <CreateEventDialog
          open={createEventDialogOpen}
          onClose={handleCreateEventDialogClosed}
          candidate={candidate}
        />
        <ConfirmConnectEmailDialog
          open={confirmConnectEmailDialogOpen}
          onClose={handleConfirmConnectGmailDialogClosed}
        />
        <RescheduleBookingDialog
          open={Boolean(rescheduleMeeting)}
          onClose={() => setRescheduleMeeting(null)}
          candidate={candidate}
          currentDatetime={rescheduleMeeting?.start_datetime || candidate.stamp}
          onRescheduled={handleRescheduleCompleted}
        />
        <RescheduleCalendarMeetingDialog
          open={Boolean(calendarRescheduleMeeting)}
          onClose={() => setCalendarRescheduleMeeting(null)}
          meeting={calendarRescheduleMeeting}
          onChanged={handleCalendarMeetingChanged}
        />
        <CancelCalendarMeetingDialog
          open={Boolean(calendarCancelMeeting)}
          onClose={() => setCalendarCancelMeeting(null)}
          meeting={calendarCancelMeeting}
          onChanged={handleCalendarMeetingChanged}
        />
      </TabHeaderRow>
      <TitledMeetingsSection
        title="Upcoming Meeting"
        accordionTitle="Scheduled Meeting"
        meetings={upcomingMeetings}
        isLoading={isLoading}
        readOnly={readOnly}
        candidate={candidate}
        onRescheduleMeeting={setRescheduleMeeting}
        onCalendarRescheduleMeeting={setCalendarRescheduleMeeting}
        onCalendarCancelMeeting={setCalendarCancelMeeting}
        emptyFallback={
          canRescheduleBooking ? (
            <BookingAppointmentFallback
              candidate={candidate}
              onRescheduleClicked={openRescheduleForCandidate}
            />
          ) : null
        }
      />
      <TitledMeetingsSection
        title="Meeting History"
        accordionTitle="Had a Meeting"
        meetings={oldMeetings}
        isLoading={isLoading}
        readOnly={readOnly}
        candidate={candidate}
        onRescheduleMeeting={setRescheduleMeeting}
        onCalendarRescheduleMeeting={setCalendarRescheduleMeeting}
        onCalendarCancelMeeting={setCalendarCancelMeeting}
      />
      <CloseableSnackbar
        open={isSnackbarOpen}
        onClose={closeSnackbar}
        message={snackbarMessage}
      />
    </TabColumn>
  )
}

function TitledMeetingsSection({
  title,
  accordionTitle,
  meetings,
  isLoading,
  readOnly,
  candidate,
  onRescheduleMeeting,
  onCalendarRescheduleMeeting,
  onCalendarCancelMeeting,
  emptyFallback = null,
}) {
  return (
    <TabContentColumn>
      <Typography variant="h5">{title}</Typography>
      {isLoading ? (
        <ActivitySkeleton />
      ) : meetings.length === 0 ? (
        emptyFallback || (
          <Typography variant="body1" color="text.secondary">
            No meetings to show here.
          </Typography>
        )
      ) : (
        meetings.map((meeting) => (
          <Meeting
            key={meeting.id}
            meeting={meeting}
            accordionTitle={accordionTitle}
            readOnly={readOnly}
            candidate={candidate}
            onRescheduleClicked={() => onRescheduleMeeting(meeting)}
            onCalendarRescheduleClicked={() =>
              onCalendarRescheduleMeeting(meeting)
            }
            onCalendarCancelClicked={() => onCalendarCancelMeeting(meeting)}
          />
        ))
      )}
    </TabContentColumn>
  )
}

function BookingAppointmentFallback({ candidate, onRescheduleClicked }) {
  return (
    <Column sx={{ gap: 1.5 }}>
      <Typography variant="body1" color="text.secondary">
        No upcoming calendar meetings, but this candidate has a booked
        appointment.
      </Typography>
      {candidate.latest_booking_reschedule?.reason ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          Last reschedule reason: {candidate.latest_booking_reschedule.reason}
        </Typography>
      ) : null}
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<EventRepeatOutlinedIcon />}
        onClick={onRescheduleClicked}
        sx={{ alignSelf: "flex-start" }}
      >
        Reschedule appointment
      </Button>
    </Column>
  )
}

// Acuity bookings are moved through the booking reschedule endpoint. Meetings
// on a Google or Outlook calendar have their own reschedule and cancel.
function canRescheduleMeeting(meeting) {
  return meeting.provider === "acuity"
}

function Meeting({
  meeting,
  accordionTitle,
  readOnly = false,
  candidate,
  onRescheduleClicked,
  onCalendarRescheduleClicked,
  onCalendarCancelClicked,
}) {
  const showReschedule = !readOnly && canRescheduleMeeting(meeting, candidate)
  const showCalendarActions = !readOnly && canChangeCalendarMeeting(meeting)
  const isCancelled = isCancelledMeeting(meeting)
  const searchParams = useSearchParams()[0]
  const urlMeetingID = parseInt(
    searchParams.get(GLOBAL_SEARCH_URL_PARAMS.meeting)
  )
  const scrollRef = useScrollToTargetElementRef(
    meeting,
    GLOBAL_SEARCH_URL_PARAMS.meeting
  )

  function getEventLink(meetingSchedule) {
    const calendarEventData = meetingSchedule.calendar_event_data
    switch (meetingSchedule.provider) {
      case GoogleAuthProvider.id:
        return calendarEventData.htmlLink
      case MicrosoftProvider.id:
        return calendarEventData.web_link
      default:
        return calendarEventData.confirmationPage
    }
  }

  function getDurationRepresentation(meeting) {
    let [hours, minutes] = meeting.duration.split(":")
    hours = parseInt(hours)
    minutes = parseInt(minutes)
    let res = ""
    if (hours > 0) {
      res += `${hours} hours`
    }
    if (minutes > 0) {
      if (hours > 0) {
        res += " and "
      }
      res += `${minutes} mins`
    }
    return res
  }

  return (
    <StyledAccordion
      defaultExpanded={urlMeetingID === meeting.id}
      key={meeting.id}
      ref={scrollRef}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Row
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <AccordionSummaryContent
            accordionTitle={isCancelled ? "Cancelled Meeting" : accordionTitle}
            subtitle={performedBySubtitle(meeting)}
            Icon={<CalendarTodayOutlinedIcon />}
            dateStr={meeting.start_datetime}
          />
          {showReschedule ? (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<EventRepeatOutlinedIcon />}
              onClick={(event) => {
                event.stopPropagation()
                onRescheduleClicked()
              }}
            >
              Reschedule
            </Button>
          ) : null}
          {showCalendarActions ? (
            <Row sx={{ gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<EventRepeatOutlinedIcon />}
                onClick={(event) => {
                  event.stopPropagation()
                  onCalendarRescheduleClicked()
                }}
              >
                Reschedule
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<EventBusyOutlinedIcon />}
                onClick={(event) => {
                  event.stopPropagation()
                  onCalendarCancelClicked()
                }}
              >
                Cancel
              </Button>
            </Row>
          ) : null}
        </Row>
      </AccordionSummary>
      <AccordionDetails>
        <Column sx={{ gap: 2 }}>
          {isCancelled ? (
            <Column sx={{ gap: 1 }}>
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label="Cancelled"
                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
              />
              {meeting.cancel_reason ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  Reason: {meeting.cancel_reason}
                </Typography>
              ) : null}
            </Column>
          ) : null}
          {meeting.is_rescheduled && !isCancelled ? (
            <Column sx={{ gap: 1 }}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label="Rescheduled"
                sx={{ alignSelf: "flex-start", fontWeight: 600 }}
              />
              {meeting.reschedule_reason ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  Reason: {meeting.reschedule_reason}
                </Typography>
              ) : null}
            </Column>
          ) : null}
          <AccordionDetailsContent>
            <AccordionDetailsTitleValue title="Duration">
              <AccordionDetailsValueTypography>
                {getDurationRepresentation(meeting)}
              </AccordionDetailsValueTypography>
            </AccordionDetailsTitleValue>
            <AccordionDetailsTitleValue title="Attendees">
              <MeetingAttendeesStack
                attendees={meeting.candidates}
                avatarSize="20px"
                fontSize="10px"
              />
            </AccordionDetailsTitleValue>
            <AccordionDetailsTitleValue title="Event link">
              <Link
                href={getEventLink(meeting)}
                variant="body1"
                target="_blank"
                sx={{ display: "flex", alignItems: "center", fontWeight: 500 }}
              >
                Click here{" "}
                <LaunchOutlinedIcon
                  variant="b500"
                  sx={{ pl: 0.5, fontSize: "18px" }}
                />
              </Link>
            </AccordionDetailsTitleValue>
          </AccordionDetailsContent>
          <AccordionDetailsTitleValue title="Booking form / notes">
            <AccordionDetailsTypography
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {meeting.description || "--"}
            </AccordionDetailsTypography>
          </AccordionDetailsTitleValue>
        </Column>
      </AccordionDetails>
    </StyledAccordion>
  )
}

export function AccordionDetailsTitleValue({ title, children }) {
  return (
    <Column gap={1}>
      <Typography variant="body1" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Column>
  )
}

export const AccordionDetailsValueTypography = styled((props) => (
  <Typography variant="h6" {...props} />
))(() => ({
  fontWeight: 600,
}))
